import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MortgageResults as MortgageResultsType } from 'utils/mortgage-calculator/mortgageCalculations';
import { buildClassName } from 'utils/build-class-name';
import cardStyles from './ui/card.module.scss';
import styles from './MortgageResults.module.scss';

interface MortgageResultsProps {
  results: MortgageResultsType;
  paymentFrequency: string;
}

const MortgageResults = ({ results, paymentFrequency }: MortgageResultsProps) => {
  const getPaymentAmount = () => {
    switch (paymentFrequency) {
      case 'biweekly':
      case 'accelerated-biweekly':
        return results.biWeeklyPayment;
      case 'weekly':
        return results.weeklyPayment;
      default:
        return results.monthlyPayment;
    }
  };

  const getFrequencyLabel = () => {
    switch (paymentFrequency) {
      case 'biweekly':
      case 'accelerated-biweekly':
        return 'Bi-weekly';
      case 'weekly':
        return 'Weekly';
      default:
        return 'Monthly';
    }
  };

  return (
    <div className={styles.component}>
      {/* Main Payment Card */}
      <Card className={buildClassName(cardStyles.shadowElevated, cardStyles.borderPrimary)}>
        <CardHeader className={styles.paymentHeader}>
          <CardTitle className={styles.paymentTitle}>
            <span>💰</span>
            Your {getFrequencyLabel()} Payment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={styles.paymentAmount}>
            ${getPaymentAmount().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <p className={styles.paymentSubtext}>
            Principal & Interest only
          </p>
        </CardContent>
      </Card>

      {/* Total Monthly Payment with Additional Costs */}
      <Card className={cardStyles.shadowSmooth}>
        <CardHeader className={styles.paymentHeader}>
          <CardTitle className={styles.paymentTitle}>
            <span>🏠</span>
            Total Monthly Payment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={styles.totalPaymentAmount}>
            ${results.totalMonthlyPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <p className={styles.paymentSubtext}>
            Including property tax, fees, and heating
          </p>
        </CardContent>
      </Card>

      {/* Breakdown Grid */}
      <div className={styles.breakdownGrid}>
        <Card className={cardStyles.shadowSmooth}>
          <CardContent style={{ paddingTop: '1.5rem' }}>
            <div className={styles.breakdownLabel}>Principal Amount</div>
            <div className={styles.breakdownValue}>
              ${Math.round(results.principalAmount).toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className={cardStyles.shadowSmooth}>
          <CardContent style={{ paddingTop: '1.5rem' }}>
            <div className={styles.breakdownLabel}>Total Interest</div>
            <div className={styles.breakdownValue}>
              ${Math.round(results.totalInterest).toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className={cardStyles.shadowSmooth}>
          <CardContent style={{ paddingTop: '1.5rem' }}>
            <div className={styles.breakdownLabel}>Total Cost</div>
            <div className={styles.breakdownValue}>
              ${Math.round(results.totalPayment).toLocaleString()}
            </div>
          </CardContent>
        </Card>

        {results.cmhcInsurance > 0 && (
          <Card className={cardStyles.shadowSmooth}>
            <CardContent style={{ paddingTop: '1.5rem' }}>
              <div className={styles.breakdownLabel}>CMHC Insurance</div>
              <div className={buildClassName(styles.breakdownValue, styles.destructive)}>
                ${Math.round(results.cmhcInsurance).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className={cardStyles.shadowSmooth}>
          <CardContent style={{ paddingTop: '1.5rem' }}>
            <div className={styles.breakdownLabel}>Land Transfer Tax</div>
            <div className={styles.breakdownValue}>
              ${Math.round(results.landTransferTax).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stress Test Warning */}
      <Card className={buildClassName(cardStyles.shadowSmooth, styles.stressTestCard)}>
        <CardContent className={styles.stressTestContent}>
          <div className={styles.stressTestHeader}>
            <span className={styles.stressTestIcon}>⚠️</span>
            <div>
              <div className={styles.stressTestTitle}>Stress Test Payment</div>
              <div className={styles.stressTestAmount}>
                ${results.stressTestPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className={styles.stressTestDescription}>
                You must qualify at this higher rate to ensure you can afford payments if rates increase
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MortgageResults;
